import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';
import AssignmentConflictModal from '../../components/AssignmentConflictModal';

const CreateCatiAssignment = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    post_id: '',
    interviewer_id: '',
    starts_at: '',
    note: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  
  // Data for dropdowns
  const [posts, setPosts] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  
  // Post search states
  const [postSearch, setPostSearch] = useState('');
  const [showPostDropdown, setShowPostDropdown] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [filteredPosts, setFilteredPosts] = useState([]);
  
  // Interviewer search states
  const [interviewerSearch, setInterviewerSearch] = useState('');
  const [showInterviewerDropdown, setShowInterviewerDropdown] = useState(false);
  const [selectedInterviewer, setSelectedInterviewer] = useState(null);
  const [filteredInterviewers, setFilteredInterviewers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Filter posts based on search
  useEffect(() => {
    const availablePosts = posts.filter(post => post.is_active && !post.current_assignment);
    
    if (postSearch.trim()) {
      const searchLower = postSearch.toLowerCase();
      const filtered = availablePosts.filter(post => 
        post.number.toString().toLowerCase().includes(searchLower) ||
        post.label?.toLowerCase().includes(searchLower)
      );
      setFilteredPosts(filtered);
    } else {
      setFilteredPosts(availablePosts);
    }
  }, [postSearch, posts]);

  // Filter interviewers based on search
  useEffect(() => {
    // Backend already filters out interviewers with assigned posts when exclude_assigned=true
    if (interviewerSearch.trim()) {
      const searchLower = interviewerSearch.toLowerCase();
      const filtered = interviewers.filter(interviewer => 
        interviewer.name.toLowerCase().includes(searchLower) ||
        interviewer.ref?.toLowerCase().includes(searchLower) ||
        interviewer.email?.toLowerCase().includes(searchLower)
      );
      setFilteredInterviewers(filtered);
    } else {
      setFilteredInterviewers(interviewers);
    }
  }, [interviewerSearch, interviewers]);

  const fetchData = async () => {
    try {
      setLoadingData(true);
      const [postsResponse, interviewersResponse] = await Promise.all([
        api.get('/cati/posts?all=true'), // Get all posts for dropdown
        api.get('/cati/interviewers?exclude_assigned=true') // Exclude interviewers with assigned posts
      ]);

      if (postsResponse.data.success) {
        // Get all posts from response (works for both paginated and all=true responses)
        const postsData = postsResponse.data.data.data || [];
        setPosts(Array.isArray(postsData) ? postsData : []);
      }

      if (interviewersResponse.data.success) {
        setInterviewers(interviewersResponse.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrors({ general: 'Erreur lors du chargement des données' });
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: value
      };
      
      
      return newFormData;
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };


  const handlePostSelect = (post) => {
    setSelectedPost(post);
    setFormData(prev => ({
      ...prev,
      post_id: post.id.toString()
    }));
    setPostSearch(`Poste ${post.number}${post.label ? ` - ${post.label}` : ''}`);
    setShowPostDropdown(false);
  };

  const handlePostSearchChange = (e) => {
    const value = e.target.value;
    setPostSearch(value);
    setShowPostDropdown(true);
    
    // Clear selection if search is cleared
    if (!value.trim()) {
      setSelectedPost(null);
      setFormData(prev => ({
        ...prev,
        post_id: ''
      }));
    }
  };

  const handlePostSearchFocus = () => {
    setShowPostDropdown(true);
  };

  const handlePostSearchBlur = () => {
    // Delay hiding to allow click on dropdown items
    setTimeout(() => {
      setShowPostDropdown(false);
    }, 200);
  };

  const handleInterviewerSelect = (interviewer) => {
    setSelectedInterviewer(interviewer);
    setFormData(prev => ({
      ...prev,
      interviewer_id: interviewer.id
    }));
    setInterviewerSearch(interviewer.name);
    setShowInterviewerDropdown(false);
  };

  const handleInterviewerSearchChange = (e) => {
    const value = e.target.value;
    setInterviewerSearch(value);
    setShowInterviewerDropdown(true);
    
    // Clear selection if search is cleared
    if (!value.trim()) {
      setSelectedInterviewer(null);
      setFormData(prev => ({
        ...prev,
        interviewer_id: ''
      }));
    }
  };

  const handleInterviewerSearchFocus = () => {
    setShowInterviewerDropdown(true);
  };

  const handleInterviewerSearchBlur = () => {
    // Delay hiding to allow click on dropdown items
    setTimeout(() => {
      setShowInterviewerDropdown(false);
    }, 200);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.post_id) {
      newErrors.post_id = 'Le poste est requis';
    }

    if (!formData.interviewer_id) {
      newErrors.interviewer_id = 'L\'enquêteur est requis';
    }

    if (!formData.starts_at) {
      newErrors.starts_at = 'La date de début est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Scroll to top to show errors if any
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/cati/assignments', {
        post_id: parseInt(formData.post_id),
        interviewer_id: parseInt(formData.interviewer_id),
        starts_at: formData.starts_at,
        note: formData.note.trim() || null
      });

      if (response.data.success) {
        setSuccessMessage('Assignation créée avec succès');
        setShowSuccessModal(true);
        setTimeout(() => {
          navigate('/cati/assignments');
        }, 2000);
      }
    } catch (error) {
      console.error('Error creating assignment:', error);
      // Scroll to top to show errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      if (error.response?.data?.error_type === 'overlapping_assignment') {
        // Show conflict modal
        setConflictData(error.response.data.conflict);
        setShowConflictModal(true);
      } else if (error.response?.data?.error_type === 'interviewer_has_active_assignment') {
        // Show error for active assignment conflict
        setErrors({ general: error.response?.data?.message || 'Cet enquêteur a déjà une assignation active' });
      } else if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.response?.data?.message || 'Erreur lors de la création de l\'assignation' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustPeriod = () => {
    setShowConflictModal(false);
    // Focus on the start date field to allow user to adjust
    const startsAtInput = document.getElementById('starts_at');
    if (startsAtInput) {
      startsAtInput.focus();
      startsAtInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleCancel = () => {
    navigate('/cati/assignments');
  };


  if (loadingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">NOUVELLE ASSIGNATION CATI</h1>
            <p className="text-gray-600">Assigner un poste à un enquêteur CATI</p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* General Error */}
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{errors.general}</p>
                </div>
              )}

              {/* Post Selection */}
              <div className="relative">
                <label htmlFor="post_search" className="block text-sm font-bold text-gray-700 mb-2">
                  Poste *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="post_search"
                    value={postSearch}
                    onChange={handlePostSearchChange}
                    onFocus={handlePostSearchFocus}
                    onBlur={handlePostSearchBlur}
                    placeholder="Rechercher un poste par numéro ou libellé..."
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                      errors.post_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {showPostDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredPosts.length > 0 ? (
                        filteredPosts.map(post => (
                          <div
                            key={post.id}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handlePostSelect(post);
                            }}
                          >
                            <div className="font-medium text-gray-900">
                              Poste {post.number}
                            </div>
                            {post.label && (
                              <div className="text-sm text-gray-500">
                                {post.label}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-gray-500 text-center">
                          {postSearch.trim() ? 'Aucun poste trouvé' : 'Tapez pour rechercher...'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.post_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.post_id}</p>
                )}
                {selectedPost && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="text-sm text-green-800">
                      <strong>Sélectionné:</strong> Poste {selectedPost.number}
                    </div>
                    {selectedPost.label && (
                      <div className="text-xs text-green-600">
                        {selectedPost.label}
                      </div>
                    )}
                  </div>
                )}
                {filteredPosts.length === 0 && !postSearch.trim() && (
                  <p className="mt-1 text-sm text-yellow-600">
                    Aucun poste libre disponible. Tous les postes actifs sont déjà assignés.
                  </p>
                )}
              </div>

              {/* Interviewer Selection */}
              <div className="relative">
                <label htmlFor="interviewer_search" className="block text-sm font-bold text-gray-700 mb-2">
                  Enquêteur *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="interviewer_search"
                    value={interviewerSearch}
                    onChange={handleInterviewerSearchChange}
                    onFocus={handleInterviewerSearchFocus}
                    onBlur={handleInterviewerSearchBlur}
                    placeholder="Rechercher un enquêteur..."
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                      errors.interviewer_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {showInterviewerDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredInterviewers.length > 0 ? (
                        filteredInterviewers.filter(interviewer => interviewer).map(interviewer => (
                          <div
                            key={interviewer.id}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleInterviewerSelect(interviewer);
                            }}
                          >
                            <div className="font-medium text-gray-900">{interviewer.name}</div>
                            <div className="text-sm text-gray-500">
                              Ref: {interviewer.ref || 'N/A'} | Email: {interviewer.email || 'N/A'}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-gray-500 text-center">
                          {interviewerSearch.trim() ? 'Aucun enquêteur trouvé' : 'Tapez pour rechercher...'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.interviewer_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.interviewer_id}</p>
                )}
                {selectedInterviewer && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="text-sm text-green-800">
                      <strong>Sélectionné:</strong> {selectedInterviewer.name}
                    </div>
                    <div className="text-xs text-green-600">
                      Ref: {selectedInterviewer.ref || 'N/A'} | Email: {selectedInterviewer.email || 'N/A'}
                    </div>
                  </div>
                )}
                {filteredInterviewers.length === 0 && !interviewerSearch.trim() && (
                  <p className="mt-1 text-sm text-yellow-600">
                    Aucun enquêteur CATI libre disponible.
                  </p>
                )}
              </div>

              {/* Start Date */}
              <div>
                <label htmlFor="starts_at" className="block text-sm font-bold text-gray-700 mb-2">
                  Date de début *
                </label>
                <input
                  type="date"
                  id="starts_at"
                  name="starts_at"
                  value={formData.starts_at}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                    errors.starts_at ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.starts_at && (
                  <p className="mt-1 text-sm text-red-600">{errors.starts_at}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Date de début de l'assignation
                </p>
              </div>

              {/* Note */}
              <div>
                <label htmlFor="note" className="block text-sm font-bold text-gray-700 mb-2">
                  Note (optionnel)
                </label>
                <textarea
                  id="note"
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Raison de l'assignation, équipe, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Information supplémentaire sur cette assignation
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.post_id || !formData.interviewer_id || !formData.starts_at}
                  className="w-full sm:w-auto flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Création...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      Créer l'assignation
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />

      {/* Assignment Conflict Modal */}
      <AssignmentConflictModal
        isOpen={showConflictModal}
        onClose={() => {
          setShowConflictModal(false);
          setConflictData(null);
        }}
        conflict={conflictData}
        onAdjustPeriod={handleAdjustPeriod}
      />
    </div>
  );
};

export default CreateCatiAssignment;
